import type { ConnectableEndpoint } from "./ConnectionEndpointProvider";

export const NAVIGATOR_GRAB_HOVER = "navigator-grab-hover";

/** Preserves the owner's visual hover while the pointer is on its external SVG grab. */
export function setConnectionGrabHover(endpoint: ConnectableEndpoint, active: boolean): void {
  if (endpoint.surface === "navigator") setNavigatorGrabHover(endpoint, active);
  else {
    const row = endpoint.hoverElement ?? endpoint.element;
    row?.classList.toggle("connectionGrabHovered", active);
    if (active && endpoint.surface === "connection-dashboard") row?.dispatchEvent(new Event("connection-dashboard-row-hover"));
  }
}

/** Recognizes an external dashboard grab without confusing repeated objects in other columns. */
export function isConnectionDashboardRowGrab(target: EventTarget | null, row: HTMLElement): boolean {
  if (!(target instanceof Element)) return false;
  const owner = target.closest("[data-connection-dashboard-grab-owner]")?.getAttribute("data-connection-dashboard-grab-owner");
  const provider = row.closest<HTMLElement>("[data-connection-provider-id]")?.dataset.connectionProviderId;
  return !!owner && !!provider && owner === `${provider}:${row.dataset.objectId}`;
}

/** Keeps row presentation in sync with its external grab and releases it on leaving. */
export function observeNavigatorGrabHover(row: HTMLElement, enter: (event: Event) => void, leave: (event: Event) => void): () => void {
  const hover = (event: Event) => {
    const active = (event as CustomEvent<boolean>).detail;
    row.classList.toggle("connectionGrabHovered", active);
    if (active) enter(event);
    else if (!row.matches(":hover")) leave(event);
  };
  row.addEventListener(NAVIGATOR_GRAB_HOVER, hover);
  return () => row.removeEventListener(NAVIGATOR_GRAB_HOVER, hover);
}

/** Bridges the overlay grab's hover to its owning navigator row, including its flyout. */
export function setNavigatorGrabHover(endpoint: ConnectableEndpoint, active: boolean): void {
  if (endpoint.surface !== "navigator" || !endpoint.element) return;
  endpoint.element.dispatchEvent(new CustomEvent(NAVIGATOR_GRAB_HOVER, { detail: active }));
}

/** Recognizes the same row's grab when the pointer leaves the normal DOM subtree. */
export function isNavigatorRowGrab(target: EventTarget | null, row: Element | null): boolean {
  return !!row?.id && target instanceof Element
    && target.closest("[data-navigator-grab-owner]")?.getAttribute("data-navigator-grab-owner") === row.id;
}
